import { useCallback, useEffect, useRef, useState } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import logo from '@/assets/logo.png';
import {
  LayoutDashboard,
  MessageSquare,
  FileText,
  Calendar,
  Wrench,
  Search,
  Download,
  LogOut,
  Menu,
  X,
  UserCircle,
  BookOpen,
  Users,
  Contact,
  Images,
  Palette,
  Sun,
  Moon,
  TrendingUp,
  ChevronDown,
  ClipboardCheck,
  ClipboardList,
  Droplets,
  Megaphone,
  Truck,
  Gauge,
  Package,
  Cable,
  MessageCircle,
  Send,
  Info,
  ShieldCheck,
  BadgeCheck,
} from 'lucide-react';
import { useAuth } from '@/hooks/useAuth';
import { useCurrentUser } from '@/hooks/useCurrentUser';
import { useTheme } from '@/hooks/useTheme';
import { useNotificationContext } from '@/context/NotificationContext';
import NotificationBell from '@/components/shared/NotificationBell';
import { cn, formatRole } from '@/lib/utils';
import type { Role } from '@/types';

interface NavItem {
  to: string;
  label: string;
  icon: React.ElementType;
}

interface NavGroup {
  heading?: string;
  items: NavItem[];
}

// Shared focus-visible ring so keyboard users get a clear indicator without
// affecting mouse interaction. Applied to every interactive element below.
const FOCUS_RING =
  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber-500 focus-visible:ring-offset-1 focus-visible:ring-offset-card';

// Pressed feedback for nav links — subtle, respects reduced-motion via motion-safe.
const PRESS = 'motion-safe:active:scale-[0.98]';

// Active nav-link treatment. Amber tint PLUS a 2px left accent bar so "current
// location" is never conveyed by colour alone (a11y: don't rely on colour). The
// bar is an inset `before:` pseudo-element so it doesn't shift the icon/label.
const NAV_LINK_ACTIVE =
  'relative bg-amber-600/[0.08] text-amber-700 dark:bg-amber-500/[0.12] dark:text-amber-300 ' +
  'before:absolute before:left-0 before:top-1.5 before:bottom-1.5 before:w-0.5 ' +
  'before:rounded-full before:bg-amber-500 dark:before:bg-amber-400';
const NAV_LINK_INACTIVE = 'text-muted-foreground hover:bg-accent';

const EXPANDED_STORAGE_KEY = 'sidebar:collapsedGroups';

// Persist which groups the user has collapsed (by heading) across sessions.
// We store the *collapsed* set so brand-new groups default to expanded.
function readCollapsedGroups(): Set<string> {
  try {
    const raw = localStorage.getItem(EXPANDED_STORAGE_KEY);
    if (!raw) return new Set();
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? new Set(parsed.filter((v) => typeof v === 'string')) : new Set();
  } catch {
    return new Set();
  }
}

function writeCollapsedGroups(collapsed: Set<string>) {
  try {
    localStorage.setItem(EXPANDED_STORAGE_KEY, JSON.stringify([...collapsed]));
  } catch {
    /* storage unavailable (private mode / quota) — non-fatal */
  }
}

const NAV_BY_ROLE: Record<Role, NavGroup[]> = {
  SUPER_ADMIN: [
    {
      items: [
        { to: '/dashboard', label: 'Dashboard', icon: LayoutDashboard },
        { to: '/search', label: 'Search', icon: Search },
      ],
    },
    {
      heading: 'Sales',
      items: [
        { to: '/customers', label: 'Customers', icon: Contact },
        { to: '/inquiries', label: 'Inquiries', icon: MessageSquare },
        { to: '/quotations', label: 'Quotations', icon: FileText },
      ],
    },
    {
      heading: 'Workshop',
      items: [
        { to: '/appointments', label: 'Appointments', icon: Calendar },
        { to: '/jobs', label: 'Jobs', icon: Wrench },
        { to: '/warranties', label: 'Warranties', icon: BadgeCheck },
        { to: '/warranty-claims', label: 'Claims', icon: ShieldCheck },
        { to: '/pending-items', label: 'Pending Items', icon: Package },
        { to: '/my-tasks', label: 'My Tasks', icon: ClipboardCheck },
      ],
    },
    {
      heading: 'Insights',
      items: [
        { to: '/kpi', label: 'KPI', icon: Gauge },
        { to: '/reports/tasks', label: 'Task Report', icon: ClipboardCheck },
        { to: '/reports/job-summary', label: 'Job Summary', icon: ClipboardList },
      ],
    },
    {
      heading: 'Finance',
      items: [
        { to: '/finance', label: 'Financial Reports', icon: TrendingUp },
        { to: '/accounting/export', label: 'Export', icon: Download },
      ],
    },
    {
      heading: 'Messaging',
      items: [{ to: '/communication', label: 'Messages', icon: MessageCircle }],
    },
    {
      heading: 'Marketing',
      items: [
        { to: '/colors', label: 'Color Wall', icon: Palette },
        { to: '/gallery', label: 'Gallery', icon: Images },
        { to: '/settings/campaigns', label: 'Campaigns', icon: Megaphone },
        { to: '/settings/whatsapp', label: 'WhatsApp Setup', icon: MessageCircle },
        { to: '/settings/whatsapp-broadcast', label: 'Broadcast', icon: Send },
      ],
    },
    {
      heading: 'Catalog',
      items: [
        { to: '/settings/catalog', label: 'Services', icon: BookOpen },
        { to: '/settings/colors', label: 'Color Catalog', icon: Palette },
        { to: '/settings/clear-coats', label: 'Clear Coats', icon: Droplets },
        { to: '/settings/suppliers', label: 'Suppliers', icon: Truck },
      ],
    },
    {
      heading: 'System',
      items: [
        { to: '/settings/users', label: 'Users', icon: Users },
        { to: '/settings/integration', label: 'Integration', icon: Cable },
        { to: '/settings/version', label: 'Version', icon: Info },
      ],
    },
  ],
  ADMIN: [
    {
      items: [
        { to: '/dashboard', label: 'Dashboard', icon: LayoutDashboard },
        { to: '/search', label: 'Search', icon: Search },
      ],
    },
    {
      heading: 'Sales',
      items: [
        { to: '/customers', label: 'Customers', icon: Contact },
        { to: '/inquiries', label: 'Inquiries', icon: MessageSquare },
        { to: '/quotations', label: 'Quotations', icon: FileText },
      ],
    },
    {
      heading: 'Workshop',
      items: [
        { to: '/appointments', label: 'Appointments', icon: Calendar },
        { to: '/jobs', label: 'Jobs', icon: Wrench },
        { to: '/warranties', label: 'Warranties', icon: BadgeCheck },
        { to: '/warranty-claims', label: 'Claims', icon: ShieldCheck },
        { to: '/pending-items', label: 'Pending Items', icon: Package },
        { to: '/my-tasks', label: 'My Tasks', icon: ClipboardCheck },
      ],
    },
    {
      heading: 'Insights',
      items: [
        { to: '/kpi', label: 'KPI', icon: Gauge },
        { to: '/reports/tasks', label: 'Task Report', icon: ClipboardCheck },
        { to: '/reports/job-summary', label: 'Job Summary', icon: ClipboardList },
      ],
    },
    {
      heading: 'Finance',
      items: [{ to: '/finance', label: 'Financial Reports', icon: TrendingUp }],
    },
    {
      heading: 'Messaging',
      items: [{ to: '/communication', label: 'Messages', icon: MessageCircle }],
    },
    {
      heading: 'Marketing',
      items: [
        { to: '/colors', label: 'Color Wall', icon: Palette },
        { to: '/gallery', label: 'Gallery', icon: Images },
        { to: '/settings/campaigns', label: 'Campaigns', icon: Megaphone },
      ],
    },
    {
      heading: 'Catalog',
      items: [
        { to: '/settings/catalog', label: 'Services', icon: BookOpen },
        { to: '/settings/colors', label: 'Color Catalog', icon: Palette },
        { to: '/settings/clear-coats', label: 'Clear Coats', icon: Droplets },
        { to: '/settings/suppliers', label: 'Suppliers', icon: Truck },
      ],
    },
    {
      heading: 'System',
      items: [{ to: '/settings/version', label: 'Version', icon: Info }],
    },
  ],
  MANAGER: [
    {
      items: [
        { to: '/dashboard', label: 'Dashboard', icon: LayoutDashboard },
        { to: '/search', label: 'Search', icon: Search },
      ],
    },
    {
      heading: 'Sales',
      items: [
        { to: '/customers', label: 'Customers', icon: Contact },
        { to: '/inquiries', label: 'Inquiries', icon: MessageSquare },
        { to: '/quotations', label: 'Quotations', icon: FileText },
      ],
    },
    {
      heading: 'Workshop',
      items: [
        { to: '/appointments', label: 'Appointments', icon: Calendar },
        { to: '/jobs', label: 'Jobs', icon: Wrench },
        { to: '/warranties', label: 'Warranties', icon: BadgeCheck },
        { to: '/warranty-claims', label: 'Claims', icon: ShieldCheck },
        { to: '/pending-items', label: 'Pending Items', icon: Package },
        { to: '/my-tasks', label: 'My Tasks', icon: ClipboardCheck },
      ],
    },
    {
      heading: 'Insights',
      items: [
        { to: '/kpi', label: 'KPI', icon: Gauge },
        { to: '/reports/tasks', label: 'Task Report', icon: ClipboardCheck },
        { to: '/reports/job-summary', label: 'Job Summary', icon: ClipboardList },
      ],
    },
    {
      heading: 'Finance',
      items: [
        { to: '/finance', label: 'Financial Reports', icon: TrendingUp },
        { to: '/accounting/export', label: 'Export', icon: Download },
      ],
    },
    {
      heading: 'Messaging',
      items: [{ to: '/communication', label: 'Messages', icon: MessageCircle }],
    },
    {
      heading: 'Marketing',
      items: [
        { to: '/colors', label: 'Color Wall', icon: Palette },
        { to: '/gallery', label: 'Gallery', icon: Images },
        { to: '/settings/campaigns', label: 'Campaigns', icon: Megaphone },
      ],
    },
    {
      heading: 'Catalog',
      items: [
        { to: '/settings/catalog', label: 'Services', icon: BookOpen },
        { to: '/settings/colors', label: 'Color Catalog', icon: Palette },
        { to: '/settings/clear-coats', label: 'Clear Coats', icon: Droplets },
        { to: '/settings/suppliers', label: 'Suppliers', icon: Truck },
      ],
    },
    {
      heading: 'System',
      items: [{ to: '/settings/version', label: 'Version', icon: Info }],
    },
  ],
  ACCOUNTANT: [
    {
      heading: 'Finance',
      items: [
        { to: '/finance', label: 'Financial Reports', icon: TrendingUp },
        { to: '/accounting/export', label: 'Export', icon: Download },
        { to: '/reports/job-summary', label: 'Job Summary', icon: ClipboardList },
      ],
    },
  ],
  LEAD: [
    {
      items: [
        { to: '/appointments', label: 'Appointments', icon: Calendar },
        { to: '/jobs', label: 'Jobs', icon: Wrench },
        // No /warranties here: the LEAD shop-floor shell has no route for it
        // (its route table redirects unknown paths to the dashboard).
        { to: '/warranty-claims', label: 'Claims', icon: ShieldCheck },
      ],
    },
  ],
  TECH: [
    {
      items: [{ to: '/jobs', label: 'Jobs', icon: Wrench }],
    },
  ],
  CUSTOMER: [],
};

function CollapsibleGroup({
  group,
  isActive,
  expanded,
  onToggle,
  onNavigate,
}: {
  group: NavGroup;
  isActive: boolean;
  expanded: boolean;
  onToggle: () => void;
  onNavigate?: () => void;
}) {
  const panelId = `nav-group-${group.heading?.toLowerCase().replace(/\s+/g, '-')}`;

  return (
    <div>
      <button
        type="button"
        onClick={onToggle}
        aria-expanded={expanded}
        aria-controls={panelId}
        className={cn(
          'flex items-center justify-between w-full px-3 py-1.5 rounded-lg text-[11px] font-semibold uppercase tracking-wider transition-colors',
          FOCUS_RING,
          isActive
            ? 'text-amber-700 dark:text-amber-400/90'
            : 'text-muted-foreground/60 hover:text-muted-foreground/80',
        )}
      >
        {group.heading}
        <ChevronDown
          size={14}
          aria-hidden="true"
          className={cn(
            'transition-transform duration-200 motion-reduce:transition-none',
            expanded ? 'rotate-0' : '-rotate-90',
          )}
        />
      </button>
      <div
        id={panelId}
        hidden={!expanded}
        className={cn(
          'overflow-hidden transition-all duration-200 motion-reduce:transition-none',
          expanded ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0',
        )}
      >
        <div className="space-y-0.5 mt-0.5">
          {group.items.map(({ to, label, icon: Icon }) => (
            <NavLink
              key={to}
              to={to}
              onClick={onNavigate}
              className={({ isActive: linkActive }) =>
                cn(
                  'flex items-center gap-3 px-3 py-1.5 rounded-lg text-sm font-medium transition-colors',
                  FOCUS_RING,
                  PRESS,
                  linkActive ? NAV_LINK_ACTIVE : NAV_LINK_INACTIVE,
                )
              }
            >
              <Icon size={16} aria-hidden="true" />
              {label}
            </NavLink>
          ))}
        </div>
      </div>
    </div>
  );
}

function NavGroups({ groups, onNavigate }: { groups: NavGroup[]; onNavigate?: () => void }) {
  const location = useLocation();
  const [collapsed, setCollapsed] = useState<Set<string>>(readCollapsedGroups);

  const toggleGroup = useCallback((heading: string) => {
    setCollapsed((prev) => {
      const next = new Set(prev);
      if (next.has(heading)) next.delete(heading);
      else next.add(heading);
      writeCollapsedGroups(next);
      return next;
    });
  }, []);

  return (
    <>
      {groups.map((group, gi) => {
        const groupHasActiveRoute = group.items.some((item) =>
          location.pathname.startsWith(item.to),
        );

        if (!group.heading) {
          return (
            <div key={gi} className="space-y-1">
              {group.items.map(({ to, label, icon: Icon }) => (
                <NavLink
                  key={to}
                  to={to}
                  onClick={onNavigate}
                  className={({ isActive }) =>
                    cn(
                      'flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium transition-colors',
                      FOCUS_RING,
                      PRESS,
                      isActive ? NAV_LINK_ACTIVE : NAV_LINK_INACTIVE,
                    )
                  }
                >
                  <Icon size={16} aria-hidden="true" />
                  {label}
                </NavLink>
              ))}
            </div>
          );
        }

        // A group stays expanded if the user hasn't explicitly collapsed it OR
        // it contains the active route (so the current page is always visible).
        const heading = group.heading;
        const expanded = groupHasActiveRoute || !collapsed.has(heading);

        // Hairline divider above every heading group except the very first one,
        // so the (now 8) sections read as distinct bands rather than one long
        // scroll. The first group never gets a top rule (nothing above it).
        return (
          <div key={gi} className={cn('mt-3.5', gi > 0 && 'border-t border-border/50 pt-3.5')}>
            <CollapsibleGroup
              group={group}
              isActive={groupHasActiveRoute}
              expanded={expanded}
              onToggle={() => toggleGroup(heading)}
              onNavigate={onNavigate}
            />
          </div>
        );
      })}
    </>
  );
}

function ThemeToggle() {
  const { theme, setTheme } = useTheme();
  const isDark = theme === 'dark';
  return (
    <button
      type="button"
      onClick={() => setTheme(isDark ? 'light' : 'dark')}
      className={cn(
        'flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium text-muted-foreground hover:bg-accent w-full transition-colors',
        FOCUS_RING,
        PRESS,
      )}
      aria-label={isDark ? 'Switch to light mode' : 'Switch to dark mode'}
    >
      {isDark ? <Sun size={18} aria-hidden="true" /> : <Moon size={18} aria-hidden="true" />}
      {isDark ? 'Light Mode' : 'Dark Mode'}
    </button>
  );
}

function BottomActions({
  onNavigate,
  onLogout,
}: {
  onNavigate?: () => void;
  onLogout: () => void;
}) {
  return (
    <div className="px-3 py-4 border-t border-border/50 space-y-1">
      <NavLink
        to="/profile"
        onClick={onNavigate}
        className={({ isActive }) =>
          cn(
            'flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium transition-colors w-full',
            FOCUS_RING,
            PRESS,
            isActive ? NAV_LINK_ACTIVE : NAV_LINK_INACTIVE,
          )
        }
      >
        <UserCircle size={18} aria-hidden="true" />
        My Profile
      </NavLink>
      <ThemeToggle />
      <button
        type="button"
        onClick={onLogout}
        className={cn(
          'flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium text-muted-foreground hover:bg-red-500/[0.06] hover:text-red-600 dark:hover:bg-red-500/[0.1] dark:hover:text-red-400 w-full transition-colors',
          FOCUS_RING,
          PRESS,
        )}
      >
        <LogOut size={18} aria-hidden="true" />
        Sign Out
      </button>
    </div>
  );
}

export default function Sidebar() {
  const { logout } = useAuth();
  const user = useCurrentUser();
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const menuButtonRef = useRef<HTMLButtonElement>(null);
  const drawerRef = useRef<HTMLElement>(null);
  const { unreadCount, notifications, soundEnabled, setSoundEnabled, markAsRead, markAllAsRead } =
    useNotificationContext();
  const navGroups = user ? (NAV_BY_ROLE[user.role] ?? []) : [];

  const allItems = navGroups.flatMap((g) => g.items);
  const currentLabel =
    allItems.find((n) => location.pathname.startsWith(n.to))?.label ??
    (location.pathname.startsWith('/profile') ? 'My Profile' : 'BengkelOS');

  // Mobile drawer: trap focus inside while open, close on Escape, and restore
  // focus to the menu button on close so keyboard users aren't dropped at the
  // top of the document.
  useEffect(() => {
    if (!open) return;
    const drawer = drawerRef.current;
    if (!drawer) return;
    // Capture the trigger now so the cleanup restores focus to the correct,
    // stable element rather than reading a possibly-changed ref later.
    const trigger = menuButtonRef.current;

    // Move focus into the drawer on open.
    const focusables = () =>
      Array.from(
        drawer.querySelectorAll<HTMLElement>(
          'a[href], button:not([disabled]), [tabindex]:not([tabindex="-1"])',
        ),
      ).filter((el) => el.offsetParent !== null);
    focusables()[0]?.focus();

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        setOpen(false);
        return;
      }
      if (e.key !== 'Tab') return;
      const items = focusables();
      if (items.length === 0) return;
      const first = items[0];
      const last = items[items.length - 1];
      const active = document.activeElement;
      if (e.shiftKey && active === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && active === last) {
        e.preventDefault();
        first.focus();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      trigger?.focus();
    };
  }, [open]);

  return (
    <>
      {/* Skip link — first focusable element; visible only when focused */}
      <a
        href="#main-content"
        className={cn(
          'sr-only focus:not-sr-only focus:fixed focus:top-3 focus:left-3 focus:z-[60]',
          'focus:rounded-lg focus:bg-amber-600 focus:px-4 focus:py-2 focus:text-sm focus:font-semibold focus:text-white focus:shadow-lg',
          FOCUS_RING,
        )}
      >
        Skip to main content
      </a>

      {/* Desktop sidebar */}
      <aside className="hidden md:flex w-56 bg-card border-r border-border/50 flex-col shrink-0">
        <div className="border-b border-border/50 px-3 py-4">
          <div className="flex flex-col items-center text-center">
            <img src={logo} alt="BRO Autopaint" className="h-11 w-11 object-contain mb-2" />
            <span className="font-bold text-[11px] leading-tight tracking-wide">
              BRO AUTOPAINT GARAGE
            </span>
            <span className="text-[9px] text-muted-foreground leading-tight mt-1">
              Shah Alam, Selangor
            </span>
          </div>
          {user && (
            <div className="flex items-center justify-center gap-2 mt-2.5">
              <span className="text-[10px] font-semibold px-2.5 py-0.5 rounded-full bg-amber-600/[0.08] text-amber-700 border border-amber-600/[0.15] dark:bg-amber-500/[0.12] dark:text-amber-400 dark:border-amber-500/[0.18] tracking-wide">
                {formatRole(user.role)}
              </span>
              <NotificationBell
                unreadCount={unreadCount}
                notifications={notifications}
                soundEnabled={soundEnabled}
                onSoundChange={setSoundEnabled}
                onMarkAsRead={markAsRead}
                onMarkAllAsRead={markAllAsRead}
                align="left"
              />
            </div>
          )}
        </div>
        <nav aria-label="Main navigation" className="flex-1 px-3 py-3 overflow-y-auto">
          <NavGroups groups={navGroups} />
        </nav>
        <BottomActions onLogout={logout} />
      </aside>

      {/* Mobile top bar */}
      <div className="md:hidden fixed top-0 left-0 right-0 z-40 bg-card/85 backdrop-blur-xl border-b border-border/50 flex items-center h-14">
        <button
          ref={menuButtonRef}
          type="button"
          onClick={() => setOpen(true)}
          aria-label="Open menu"
          aria-haspopup="dialog"
          aria-expanded={open}
          className={cn(
            'p-1.5 ml-2 rounded-lg text-muted-foreground hover:bg-accent shrink-0',
            FOCUS_RING,
          )}
        >
          <Menu size={22} aria-hidden="true" />
        </button>
        <img src={logo} alt="BRO Autopaint" className="h-9 w-9 object-contain mx-2 shrink-0" />
        <div className="flex flex-col justify-center min-w-0 flex-1">
          <span className="font-bold text-[11px] leading-tight truncate">BRO AUTOPAINT GARAGE</span>
          <span className="text-[9px] text-muted-foreground leading-tight truncate">
            {currentLabel}
          </span>
        </div>
        <div className="mr-2">
          <NotificationBell
            unreadCount={unreadCount}
            notifications={notifications}
            soundEnabled={soundEnabled}
            onSoundChange={setSoundEnabled}
            onMarkAsRead={markAsRead}
            onMarkAllAsRead={markAllAsRead}
          />
        </div>
      </div>

      {/* Mobile drawer */}
      {open && (
        <div className="md:hidden fixed inset-0 z-50 flex" onClick={() => setOpen(false)}>
          <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" aria-hidden="true" />
          <aside
            ref={drawerRef}
            role="dialog"
            aria-modal="true"
            aria-label="Navigation menu"
            className="relative w-72 max-w-[85vw] bg-card flex flex-col h-full shadow-xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="border-b border-border/50 px-3 py-4">
              <div className="flex items-start justify-between">
                <div className="flex flex-col items-center text-center flex-1">
                  <img src={logo} alt="BRO Autopaint" className="h-11 w-11 object-contain mb-2" />
                  <span className="font-bold text-[11px] leading-tight tracking-wide">
                    BRO AUTOPAINT GARAGE
                  </span>
                  <span className="text-[9px] text-muted-foreground leading-tight mt-1">
                    Shah Alam, Selangor
                  </span>
                </div>
                <button
                  type="button"
                  onClick={() => setOpen(false)}
                  className={cn(
                    'p-1.5 -mr-1 -mt-1 rounded-lg text-muted-foreground hover:bg-accent shrink-0',
                    FOCUS_RING,
                  )}
                  aria-label="Close menu"
                >
                  <X size={20} aria-hidden="true" />
                </button>
              </div>
              {user && (
                <div className="flex justify-center mt-2.5">
                  <span className="text-[10px] font-semibold px-2.5 py-0.5 rounded-full bg-amber-600/[0.08] text-amber-700 border border-amber-600/[0.15] dark:bg-amber-500/[0.12] dark:text-amber-400 dark:border-amber-500/[0.18] tracking-wide">
                    {formatRole(user.role)}
                  </span>
                </div>
              )}
            </div>
            <nav aria-label="Main navigation" className="flex-1 px-3 py-3 overflow-y-auto">
              <NavGroups groups={navGroups} onNavigate={() => setOpen(false)} />
            </nav>
            <BottomActions
              onNavigate={() => setOpen(false)}
              onLogout={() => {
                logout();
                setOpen(false);
              }}
            />
          </aside>
        </div>
      )}
    </>
  );
}
